import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getLocalAgentConfig } from '../../api/client';
import { useState, useEffect } from 'react';
import { UserProfileModal } from '../UserProfileModal';
import { useChatHistory } from '../../hooks/useChatHistory';

const NAV_ITEMS = [
  { to: '/nodes',     label: 'AI Nodes',  icon: '🤖', adminOnly: true },
  { to: '/test',      label: 'Live Test', icon: '⚡', adminOnly: true },
  { to: '/projects',  label: 'Projects',  icon: '🗂', adminOnly: true },
];

const WORKER_URL = import.meta.env.VITE_API_BASE ?? window.location.origin;

export function Layout() {
  const { user, logout } = useAuth();
  const isPaidUser = user?.role === 'admin' || (user as any)?.is_paid;
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);

  const { 
    conversations, 
    activeId, 
    activeProjectId,
    projects, 
    localWorkspaces,
    createConversation, 
    deleteConversation, 
    setActiveId,
    loadProjects,
    setActiveProjectId
  } = useChatHistory();

  const [ericMecToken, setEricMecToken] = useState(() => localStorage.getItem('eric_mec_local_token'));
  const [ericMecWorkspaces, setEricMecWorkspaces] = useState<string[]>(() => {
    const stored = localStorage.getItem('eric_mec_workspaces');
    if (stored) {
      try { return JSON.parse(stored); } catch (e) { return []; }
    }
    const old = localStorage.getItem('eric_mec_workspace');
    if (old) {
      localStorage.setItem('eric_mec_workspaces', JSON.stringify([old]));
      return [old];
    }
    return [];
  });
  const [availableAgents, setAvailableAgents] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [executionMode, setExecutionMode] = useState<'cloud' | 'local'>(() => {
    return (localStorage.getItem('ericoder_execution_mode') as 'cloud' | 'local') || 'cloud';
  });

  const toggleExecutionMode = () => {
    const nextMode = executionMode === 'cloud' ? 'local' : 'cloud';
    if (nextMode === 'local') {
      const agent = getLocalAgentConfig();
      if (!agent) {
        if (!confirm('⚠️ Chế độ Local Sovereign yêu cầu phần mềm Eric_MEC_ComputeUse đang chạy trên máy tính của bạn để xử lý file/command trực tiếp mà không đưa code lên cloud.\n\nBạn có muốn chuyển sang chế độ này không?')) {
          return;
        }
      }
    }
    setExecutionMode(nextMode);
    localStorage.setItem('ericoder_execution_mode', nextMode);
    window.dispatchEvent(new Event('storage'));
  };

  const handleUploadWorkspaceToCloud = async () => {
    const agent = getLocalAgentConfig();
    const targetWs = agent?.workspaces?.length > 0 ? agent.workspaces[0] : null;
    if (!targetWs) {
      alert('Không tìm thấy thư mục làm việc hoạt động.');
      return;
    }
    if (!confirm(`Bạn có chắc chắn muốn nén và tải lên dự án '${targetWs}' lên EricMEC để thiết lập chỉ mục tìm kiếm ngữ nghĩa (RAG Index)?\n\nViệc này giúp AI hiểu toàn bộ codebase và tiết kiệm tới 80-90% lượng token khi trao đổi.`)) {
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('ericoder_token');
    
    try {
      const url = agent?.tunnel_url ? `${agent.tunnel_url}/zip_workspace` : 'http://localhost:8080/zip_workspace';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (agent?.agent_token) headers['Authorization'] = `Bearer ${agent.agent_token}`;

      // 1. Gọi local agent để nén thư mục dự án hiện tại
      const zipRes = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ session_id: 'upload_sso' })
      });
      
      if (!zipRes.ok) {
        throw new Error('Local agent nén thư mục thất bại. Vui lòng kiểm tra Eric MEC Desktop App đang chạy.');
      }
      
      const zipBlob = await zipRes.blob();
      
      // 2. Gửi file zip lên Gateway để tạo dự án mới và RAG index
      const formData = new FormData();
      formData.append('name', targetWs);
      formData.append('description', `Dự án được tải lên từ local workspace của Eric MEC`);
      formData.append('file', zipBlob, `${targetWs}.zip`);
      formData.append('tech_stack', JSON.stringify([]));
      
      const uploadRes = await fetch(`${WORKER_URL}/api/projects`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData
      });
      
      if (!uploadRes.ok) {
        const errData = await uploadRes.json().catch(() => ({}));
        throw new Error(errData.error || `Lỗi tải lên Gateway (HTTP ${uploadRes.status})`);
      }
      
      const jsonRes = await uploadRes.json();
      
      // Auto-reload projects and open new chat for the uploaded project
      loadProjects();
      const targetProjId = jsonRes.project?.id || targetWs;
      setActiveProjectId(targetProjId);
      createConversation(targetProjId);
      navigate('/chat');
      
      alert(`🎉 Thành công! Dự án '${targetWs}' đã được tải lên EricMEC.\nHệ thống đã mở một phiên chat mới cho dự án này và đang đánh chỉ mục RAG Index.`);
    } catch (err: any) {
      alert(`Lỗi tải lên: ${err.message || err}`);
    } finally {
      setUploading(false);
    }
  };

  // Persist MEC token to KV so any tab/reload can see it
  const persistMecStatus = async (mecToken: string, mecWorkspaces: string[]) => {
    const authToken = localStorage.getItem('ericoder_token');
    if (!authToken || !mecToken) return;
    try {
      const activeAgent = getLocalAgentConfig();
      await fetch(`${WORKER_URL}/api/user/mec-status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          agent_id: activeAgent?.agent_id || 'legacy-sso-agent', 
          hostname: activeAgent?.hostname || 'Local PC', 
          tunnel_url: activeAgent?.tunnel_url || 'http://localhost:8080', 
          agent_token: mecToken, 
          workspaces: mecWorkspaces 
        })
      });
    } catch (_) { /* ignore */ }
  };

  // Poll for Eric MEC status synced from backend
  useEffect(() => {
    let interval: any;
    const checkMecStatus = async () => {
      const authToken = localStorage.getItem('ericoder_token');
      if (authToken) {
        try {
          // If we just got a local token, try to persist it first before polling
          const localToken = localStorage.getItem('eric_mec_local_token');
          const localWsStr = localStorage.getItem('eric_mec_workspaces');
          let localWs: string[] = [];
          try { if (localWsStr) localWs = JSON.parse(localWsStr); } catch(e) {}
          
          if (localToken && !localStorage.getItem('eric_mec_synced')) {
             await persistMecStatus(localToken, localWs);
             localStorage.setItem('eric_mec_synced', 'true');
          }

          const res = await fetch(`${WORKER_URL}/api/user/mec-status`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (res.ok) {
            const data = await res.json();
            // Expected dict: { [agent_id]: { agent_id, hostname, tunnel_url, agent_token, workspaces } }
            const agentIds = Object.keys(data || {});
            let activeAgent = getLocalAgentConfig();
            
            if (agentIds.length > 0) {
              if (!activeAgent || !data[activeAgent.agent_id]) {
                activeAgent = data[agentIds[0]];
                localStorage.setItem('eric_mec_active_agent', JSON.stringify(activeAgent));
              } else {
                activeAgent = data[activeAgent.agent_id];
                localStorage.setItem('eric_mec_active_agent', JSON.stringify(activeAgent));
              }
              if (activeAgent?.workspaces && activeAgent.workspaces.length > 0) {
                localStorage.setItem('eric_mec_workspaces', JSON.stringify(activeAgent.workspaces));
                setEricMecWorkspaces(activeAgent.workspaces);
              }
              if (activeAgent?.agent_token) {
                localStorage.setItem('eric_mec_local_token', activeAgent.agent_token);
                setEricMecToken(activeAgent.agent_token);
              }
              setAvailableAgents(Object.values(data));
              window.dispatchEvent(new Event('storage'));
            }
          }
        } catch (e) {
          // ignore network hiccups
        }
      }

      // Heartbeat ping (non-destructive - preserves workspace configs)
      const activeAgent = getLocalAgentConfig();
      if (activeAgent?.tunnel_url) {
        try {
          await fetch(`${activeAgent.tunnel_url}/status`, { method: 'GET', signal: AbortSignal.timeout(5000) });
        } catch (err) {
          // Tunnel ping warning without destructive removal
          console.debug('[EricMEC] Tunnel status ping skipped or timed out');
        }
      }
    };

    checkMecStatus();
    interval = setInterval(checkMecStatus, 15000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ericMecToken]); // ericMecWorkspaces intentionally omitted — it's an array ref that changes every poll cycle

  // Sync token changes across tabs
  useEffect(() => {
    const syncToken = () => {
      const activeAgent = getLocalAgentConfig();
      if (activeAgent) {
        setEricMecToken(activeAgent.agent_token);
        setEricMecWorkspaces(activeAgent.workspaces || []);
      } else {
        setEricMecToken(null);
        setEricMecWorkspaces([]);
      }
      const curMode = localStorage.getItem('ericoder_execution_mode');
      if (curMode === 'local' || curMode === 'cloud') {
        setExecutionMode(curMode);
      }
    };
    window.addEventListener('storage', syncToken);
    return () => window.removeEventListener('storage', syncToken);
  }, []);

  return (
    <div className="app-layout">
      {/* Header */}
      <header className="app-header">
        {/* Status pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: 'var(--accent-green)',
            boxShadow: '0 0 6px var(--accent-green)',
            display: 'inline-block',
            animation: 'pulse-dot 2s ease-in-out infinite',
          }} />
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {WORKER_URL.replace('https://', '').replace('http://', '')}
          </span>

          {/* Dual-Engine 1-Click Toggle */}
          <div
            onClick={toggleExecutionMode}
            title={
              executionMode === 'local'
                ? '🔒 Local Sovereign Mode: Code 100% ở lại máy bạn, chạy qua Eric_MEC_ComputeUse. Bấm để đổi sang Cloud Sandbox.'
                : '⚡ Cloud Sandbox Mode: Máy ảo Fly.io đám mây với snapshot R2, tiện làm việc từ xa trên mọi máy. Bấm để đổi sang Local Sovereign.'
            }
            style={{
              marginLeft: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 10px',
              background: executionMode === 'local' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(56, 189, 248, 0.15)',
              border: `1px solid ${executionMode === 'local' ? 'rgba(16, 185, 129, 0.5)' : 'rgba(56, 189, 248, 0.5)'}`,
              borderRadius: '20px',
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: '11px',
              fontWeight: 600,
              color: executionMode === 'local' ? '#34d399' : '#38bdf8',
              transition: 'all 0.2s ease',
              boxShadow: executionMode === 'local' ? '0 0 8px rgba(16, 185, 129, 0.2)' : '0 0 8px rgba(56, 189, 248, 0.2)',
            }}
          >
            <span style={{ fontSize: '13px' }}>{executionMode === 'local' ? '🔒' : '⚡'}</span>
            <span>{executionMode === 'local' ? 'Local Sovereign' : 'Cloud Sandbox'}</span>
            <span style={{
              fontSize: '9px',
              padding: '1px 5px',
              borderRadius: '8px',
              background: executionMode === 'local' ? '#059669' : '#0284c7',
              color: '#fff',
              marginLeft: '2px',
              textTransform: 'uppercase',
            }}>
              {executionMode === 'local' ? 'PC' : 'Cloud'}
            </span>
          </div>
          
          {ericMecToken ? (
             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                 <select 
                   value={getLocalAgentConfig()?.agent_id || ''}
                   onChange={(e) => {
                     const selected = availableAgents.find(a => a.agent_id === e.target.value);
                     if (selected) {
                       localStorage.setItem('eric_mec_active_agent', JSON.stringify(selected));
                       localStorage.setItem('eric_mec_workspaces', JSON.stringify(selected.workspaces || []));
                       localStorage.setItem('eric_mec_local_token', selected.agent_token || '');
                       window.dispatchEvent(new Event('storage'));
                       setEricMecToken(selected.agent_token);
                       setEricMecWorkspaces(selected.workspaces || []);
                     }
                   }}
                   title={`Local Exec Tunnel: Online\nClick to change active agent`}
                   style={{
                     marginLeft: '12px',
                     padding: '4px 28px 4px 10px',
                     background: 'rgba(16,185,129,0.1)',
                     border: '1px solid rgba(16,185,129,0.4)',
                     borderRadius: '20px',
                     color: '#4ade80',
                     fontSize: '11px',
                     fontWeight: 600,
                     cursor: 'pointer',
                     outline: 'none',
                     appearance: 'none',
                     backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%234ade80%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                     backgroundRepeat: 'no-repeat',
                     backgroundPosition: 'right 8px center',
                     boxShadow: '0 0 8px rgba(16,185,129,0.15)'
                   }}
                 >
                   {availableAgents.map(a => (
                     <option key={a.agent_id} value={a.agent_id} style={{ background: '#1e293b', color: '#f8fafc' }}>
                       🟢 {a.hostname || a.agent_id} {a.workspaces?.length > 0 ? `| 📂 ${a.workspaces.length} Projects` : ''}
                     </option>
                   ))}
                 </select>

               <button
                 disabled={uploading}
                 onClick={handleUploadWorkspaceToCloud}
                 title="Nén & Tải codebase hiện tại lên EricMEC để lập RAG Index (Tiết kiệm 80-90% token AI)"
                 style={{
                   display: 'flex',
                   alignItems: 'center',
                   gap: '4px',
                   padding: '4px 12px',
                   background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                   border: '1px solid rgba(56,189,248,0.4)',
                   borderRadius: '20px',
                   color: '#ffffff',
                   fontSize: '11px',
                   fontWeight: 600,
                   cursor: uploading ? 'not-allowed' : 'pointer',
                   transition: 'all 0.2s',
                   boxShadow: '0 0 8px rgba(2,132,199,0.25)',
                   opacity: uploading ? 0.7 : 1
                 }}
                 onMouseEnter={e => {
                   if (!uploading) {
                     e.currentTarget.style.boxShadow = '0 0 12px rgba(2,132,199,0.5)';
                     e.currentTarget.style.transform = 'translateY(-1px)';
                   }
                 }}
                 onMouseLeave={e => {
                   if (!uploading) {
                     e.currentTarget.style.boxShadow = '0 0 8px rgba(2,132,199,0.25)';
                     e.currentTarget.style.transform = 'translateY(0)';
                   }
                 }}
               >
                 {uploading ? (
                   <>
                     <span className="spinner" style={{ width: '10px', height: '10px', display: 'inline-block', borderWidth: '1px' }} />
                     Uploading...
                   </>
                 ) : (
                   <>📤 Upload Project (RAG Index)</>
                 )}
               </button>
             </div>
          ) : (
             <a 
               href="https://mec.wowerix.com/dashboard"
               target="_blank"
               style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: 'rgba(56,189,248,0.05)', border: '1px dashed rgba(56,189,248,0.3)', borderRadius: '20px', color: '#38bdf8', fontSize: '11px', fontWeight: 600, textDecoration: 'none', transition: 'all 0.2s' }}
               onMouseEnter={e => e.currentTarget.style.background = 'rgba(56,189,248,0.1)'}
               onMouseLeave={e => e.currentTarget.style.background = 'rgba(56,189,248,0.05)'}
             >
               🔗 Connect Eric MEC
             </a>
          )}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Deployments button */}
          <NavLink
            to="/deployments"
            title="Deployments"
            style={({ isActive }) => ({
              background: isActive ? 'var(--bg-elevated)' : 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '6px',
              textDecoration: 'none',
              color: isActive ? 'var(--accent-cyan)' : 'var(--text-secondary)',
              transition: 'background 0.2s, color 0.2s',
            })}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--bg-elevated)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseOut={(e) => {
              const isActive = e.currentTarget.classList.contains('active');
              e.currentTarget.style.background = isActive ? 'var(--bg-elevated)' : 'none';
              e.currentTarget.style.color = isActive ? 'var(--accent-cyan)' : 'var(--text-secondary)';
            }}
          >
            <span style={{ fontSize: '18px' }}>🚀</span>
          </NavLink>

          {/* Profile button (Brightened) */}
          <button 
            onClick={() => setShowProfile(true)}
            style={{ 
              background: 'rgba(124, 58, 237, 0.15)',
              border: '1px solid rgba(124, 58, 237, 0.4)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              borderRadius: '20px',
              color: '#ffffff',
              fontWeight: 600,
              transition: 'all 0.2s',
              boxShadow: '0 0 10px rgba(124, 58, 237, 0.2)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(124, 58, 237, 0.25)';
              e.currentTarget.style.borderColor = 'rgba(124, 58, 237, 0.6)';
              e.currentTarget.style.boxShadow = '0 0 15px rgba(124, 58, 237, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(124, 58, 237, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(124, 58, 237, 0.4)';
              e.currentTarget.style.boxShadow = '0 0 10px rgba(124, 58, 237, 0.2)';
            }}
          >
            <span style={{ fontSize: '14px' }}>👤</span>
            <span style={{ fontSize: '13px' }}>
              {user?.username || 'User'}
            </span>
            <span className={`badge badge--${user?.role === 'admin' ? 'purple' : 'blue'}`} style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(255,255,255,0.15)', color: '#ffffff' }}>
              {user?.role || 'user'}
            </span>
          </button>
          <button className="btn btn--secondary btn--sm" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="app-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div className="nav-logo">
          <img src="/logo.png" alt="Ericoder Logo" style={{ width: '40px', height: '40px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
          <div>
            <span className="nav-logo__name">Ericoder</span>
            <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '1px' }}>AI Software Factory</div>
          </div>
        </div>

        {/* New Conversation Button */}
        <div style={{ padding: '0 4px' }}>
          <button
            onClick={() => {
              if (!isPaidUser) {
                alert('Hãy nâng cấp tài khoản trả phí để sử dụng tính năng này');
                return;
              }
              const activeConv = conversations.find(c => c.id === activeId);
              const targetProjId = activeConv?.projectId || activeProjectId || (localWorkspaces && localWorkspaces[0]) || (projects && projects[0]?.id) || 'default';
              createConversation(targetProjId);
              navigate('/chat');
            }}
            style={{
              padding: '10px 16px',
              width: '100%',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(6,182,212,0.25)',
              background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '12px'
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>+</span> New Conversation
          </button>

          <NavLink
            to="/dashboard"
            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              textDecoration: 'none',
              transition: 'all 0.2s'
            }}
          >
            <span className="nav-item__icon">📊</span>
            Dashboard
          </NavLink>
        </div>

        {/* Projects and Chats */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
          <div className="nav-section__label" style={{ padding: '4px 8px 2px' }}>
            Project &amp; Chats
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 4px' }}>
            {!isPaidUser ? (
              <div 
                style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', cursor: 'pointer', background: 'rgba(255,255,255,0.02)', borderRadius: '6px' }}
                onClick={() => alert('Hãy nâng cấp tài khoản trả phí để sử dụng tính năng này')}
              >
                Lịch sử chat đã bị khoá.<br/><br/>
                <span style={{ color: 'var(--accent-cyan)' }}>Nâng cấp tài khoản</span> để sử dụng.
              </div>
            ) : (() => {
              const allActiveProjects = [
                ...(localWorkspaces || []).map(ws => ({ id: ws, name: ws.split(/[\\/]/).pop() || ws, isLocal: true })),
                ...projects.map(p => ({ ...p, isLocal: false }))
              ];

              // Only explicitly 'default' conversations go to Default Project.
              // Conversations belonging to disconnected projects will be naturally hidden.
              const hasDefaultConvs = conversations.some(c => c.projectId === 'default');
              const projectList = [...allActiveProjects];
              if (hasDefaultConvs && !projectList.some(p => p.id === 'default')) {
                projectList.push({ id: 'default', name: 'Default Project', isLocal: false });
              }
              if (projectList.length === 0) {
                projectList.push({ id: 'default', name: 'Default Project', isLocal: false });
              }

              return projectList.map(project => {
                const projectConvs = conversations.filter(c => 
                  c.projectId === project.id || 
                  c.projectId.endsWith('\\' + project.name) || 
                  c.projectId.endsWith('/' + project.name) ||
                  project.id.endsWith('\\' + c.projectId) || 
                  project.id.endsWith('/' + c.projectId)
                );
                
                return (
                  <div key={`${project.isLocal ? 'local' : 'cloud'}-${project.id}`} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 8px',
                      color: 'var(--text-secondary)',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '4px',
                      background: 'rgba(255,255,255,0.02)',
                      marginBottom: '4px'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {project.id === 'default' ? '📁' : project.isLocal ? '💻' : '📦'} {project.name}
                      </span>
                      <button
                        title="New Chat in Project"
                        onClick={(e) => {
                          e.stopPropagation();
                          createConversation(project.id);
                          navigate('/chat');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '0 4px'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-cyan)'}
                        onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                      >
                        +
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingLeft: '8px', marginBottom: '8px' }}>
                      {projectConvs.length === 0 ? (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '4px 8px', fontStyle: 'italic' }}>
                          No conversations
                        </div>
                      ) : (
                        projectConvs.map(c => {
                          const isActive = activeId === c.id;
                          return (
                            <div
                              key={c.id}
                              onClick={(e) => {
                                if (!isPaidUser) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  alert('Hãy nâng cấp tài khoản trả phí để xem lịch sử đoạn chat');
                                  return;
                                }
                                setActiveId(c.id);
                                navigate('/chat');
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '6px 8px',
                                borderRadius: '4px',
                                fontSize: '12.5px',
                                color: isActive ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                                background: isActive ? 'rgba(6,182,212,0.1)' : 'transparent',
                                borderLeft: isActive ? '2px solid var(--accent-cyan)' : '2px solid transparent',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                overflow: 'hidden'
                              }}
                              onMouseOver={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.background = 'var(--bg-glass-hover)';
                                  e.currentTarget.style.color = 'var(--text-primary)';
                                }
                                const delBtn = e.currentTarget.querySelector('.del-chat-btn');
                                if (delBtn) (delBtn as HTMLElement).style.opacity = '1';
                              }}
                              onMouseOut={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = 'var(--text-secondary)';
                                }
                                const delBtn = e.currentTarget.querySelector('.del-chat-btn');
                                if (delBtn) (delBtn as HTMLElement).style.opacity = '0';
                              }}
                            >
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, paddingRight: '8px' }}>
                                💬 {c.title}
                              </span>
                              <button
                                className="del-chat-btn"
                                title="Delete Conversation"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm('Delete this conversation?')) {
                                    deleteConversation(c.id);
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--accent-red)',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                  padding: '0 2px'
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Platform & Admin Navigation */}
        <div className="nav-section" style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
          <div className="nav-section__label">Platform</div>
          {NAV_ITEMS.filter(item => !item.adminOnly || user?.role === 'admin').map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
            >
              <span className="nav-item__icon">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </div>

        {user?.role === 'admin' && (
          <div className="nav-section" style={{ marginTop: '0' }}>
            <div className="nav-section__label">Admin</div>
            <NavLink to="/admin/users" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <span className="nav-item__icon">👥</span>
              Users & Quota
            </NavLink>
            <NavLink to="/admin/plans" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <span className="nav-item__icon">💎</span>
              Billing Plans
            </NavLink>
          </div>
        )}

      </aside>

      {/* Main content */}
      <main className="app-content">
        <Outlet />
      </main>

      {showProfile && <UserProfileModal onClose={() => setShowProfile(false)} />}
    </div>
  );
}
